/**
 * LinkedIn Insight Tag conversion tracking utilities
 *
 * Mirrors the pattern from facebookPixel.ts and googleAnalytics.ts.
 * Requires the LinkedIn Insight Tag to be loaded (via Astro layouts).
 *
 * Conversion IDs must be created in LinkedIn Campaign Manager:
 *   Campaign Manager → Analyze → Conversion tracking → Create conversion
 */

import { syncTrackingCookies } from './trackingCookies'

declare global {
  interface Window {
    lintrk?: (action: string, data: Record<string, unknown>) => void
  }
}

/**
 * Fire a LinkedIn conversion event
 */
export const trackLinkedInConversion = (conversionId: string): void => {
  if (typeof window !== 'undefined' && window.lintrk) {
    window.lintrk('track', { conversion_id: conversionId })

    if (import.meta.env.DEV) {
      console.log('[LinkedIn]', 'conversion', conversionId)
    }
  }
}

// Conversion IDs — update these after creating conversion actions in Campaign Manager
// Go to: Campaign Manager → Analyze → Conversion Tracking → Create Conversion
// Set type to "Specific event" and note the conversion_id
const CONVERSION_IDS = {
  registration: '27065041',
  purchase: '27065049', 
  key_page_view: '27065057', 
}

/**
 * Track user registration
 */
export const trackLinkedInRegistration = (): void => {
  if (CONVERSION_IDS.registration) {
    trackLinkedInConversion(CONVERSION_IDS.registration)
  }
}

/**
 * Track purchase/subscription
 */
export const trackLinkedInPurchase = (): void => {
  if (CONVERSION_IDS.purchase) {
    trackLinkedInConversion(CONVERSION_IDS.purchase)
  }
}

/**
 * Track key page view (e.g., pricing page)
 */
export const trackLinkedInKeyPageView = (): void => {
  if (CONVERSION_IDS.key_page_view) {
    trackLinkedInConversion(CONVERSION_IDS.key_page_view)
  }
}

/**
 * Initialize LinkedIn tracking on app load.
 * Syncs li_fat_id cookie into jt_tracking for server-side use.
 */
export const initLinkedInTracking = (): void => {
  // Sync tracking cookies (captures li_fat_id, _fbp, _fbc)
  syncTrackingCookies()
}
